<template>
  <div
    :class="{
      busy: isBusy || isWaitingForValues || loading,
      'document-metadata-tags-input': true,
    }"
  >
    <PvMultiSelect
      :model-value="localTagValues"
      :options="possibleValues ?? []"
      :filter="true"
      :auto-filter-focus="true"
      :disabled="disabled || isWaitingForValues || loading"
      :class="{ 'p-invalid': !isInputValid }"
      fluid
      @update:model-value="handleTagsChanged"
    >
      <template #value="{ value }">
        <div v-if="value" class="flex flex-wrap gap-1 items-center">
          <Tag
            v-for="item in value"
            :key="item"
            class="flex max-w-full"
            :style="'background-color: color-mix(in srgb, var(--p-primary-500), transparent 84%); color: var(--p-primary-500); font-weight: 500;'"
          >
            <div class="flex flex-wrap items-center gap-1">
              <span class="text-wrap">{{ item }}</span>
              <font-awesome-icon
                class="tag-remove-icon"
                :icon="faX"
                @click.stop="
                  handleTagsChanged(
                    (value as string[]).filter((v) => v !== item),
                  )
                "
              />
            </div>
          </Tag>
        </div>
        <div v-else class="p-placeholder">
          <span class="p-placeholder-text">d</span>
        </div>
      </template>
    </PvMultiSelect>
  </div>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { useToast } from '@/composables/useToast';
  import PvMultiSelect from 'primevue/multiselect';
  import Tag from 'primevue/tag';
  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
  import { faX } from '@fortawesome/pro-regular-svg-icons';
  import { DEFAULT_METADATA_VALUES_SEPARATOR } from 'types';
  import {
    ReloadMetadataFunction,
    UpdateMetadataFunction,
    ValidationFunction,
  } from '../DocumentsMetadata/DocumentsMetadata.types';
  interface DocumentMetadataTagInputProps {
    disabled?: boolean;
    isWaitingForValues?: boolean;
    currentValues: string;
    possibleValues?: string[];
    metadataField: string;
    loading?: boolean;
    validationFunction?: ValidationFunction;
    getMetadataFunction?: ReloadMetadataFunction;
    updateMetadataFunction: UpdateMetadataFunction;
  }

  const props = withDefaults(defineProps<DocumentMetadataTagInputProps>(), {
    disabled: false,
    isWaitingForValues: false,
  });

  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
  }>();

  const { createErrorToast } = useToast();

  const isBusy = ref(false);
  const isInputValid = ref(true);

  const localTagValues = ref<string[]>(
    props.currentValues === ''
      ? []
      : props.currentValues.split(DEFAULT_METADATA_VALUES_SEPARATOR),
  );

  watch(
    () => props.currentValues,
    (val) => {
      localTagValues.value =
        val === '' ? [] : val.split(DEFAULT_METADATA_VALUES_SEPARATOR);
    },
  );

  function validateInput(): void {
    if (props.validationFunction) {
      isInputValid.value = props.validationFunction(props.currentValues);
    }
  }

  async function handleTagsChanged(tags: string[] | undefined) {
    if (!tags) return;
    localTagValues.value = tags;
    try {
      isBusy.value = true;
      await props.updateMetadataFunction(props.metadataField, tags);
    } catch {
      localTagValues.value =
        props.currentValues === ''
          ? []
          : props.currentValues.split(DEFAULT_METADATA_VALUES_SEPARATOR);
      createErrorToast();
    } finally {
      isBusy.value = false;
      validateInput();
      emit('document-data-changed');
    }
  }

  watch(
    () => [props.currentValues, props.validationFunction],
    () => {
      validateInput();
    },
    { immediate: true },
  );
</script>

<style lang="scss" scoped>
  .document-metadata-tags-input {
    width: 100%;

    &.busy {
      :deep(.p-multiselect) {
        @include pulsating-animation;
      }
    }

    :deep(.p-multiselect.p-invalid) {
      border-color: var(--p-inputtext-invalid-border-color);
    }
  }
</style>
